import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import { ConfirmDialog } from '../components/UI/RowActionMenu'
import {
  getZoneSpotlights,
  toggleZoneSpotlight,
  deleteZoneSpotlight
} from '../apollo'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Alert,
  Box,
  Container,
  IconButton,
  Modal,
  Switch,
  Button
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { modalOverlay, modalCard } from '../utils/modalStyles'
import { ReactComponent as CouponsIcon } from '../assets/svg/svg/Coupons.svg'
import TableHeader from '../components/TableHeader'
import ZoneSpotlightComponent from '../components/ZoneSpotlight/ZoneSpotlight'

const GET_ZONE_SPOTLIGHTS = gql`
  ${getZoneSpotlights}
`
const TOGGLE_ZONE_SPOTLIGHT = gql`
  ${toggleZoneSpotlight}
`
const DELETE_ZONE_SPOTLIGHT = gql`
  ${deleteZoneSpotlight}
`

const ZoneSpotlights = props => {
  const { t } = props
  const [deleteTarget, setDeleteTarget] = useState(null)
  const globalClasses = useGlobalStyles()

  // The dialog reuses the same form: it upserts on zone + shop type, so
  // handing it that pair is all it takes to edit the saved row.
  const [editTarget, setEditTarget] = useState(null)
  const [addModal, setAddModal] = useState(false)
  // The dialog closes on a successful save, so the confirmation belongs out
  // here where the admin lands rather than on the form that just went away.
  const [savedMessage, setSavedMessage] = useState('')

  const closeEdit = message => {
    setEditTarget(null)
    setSavedMessage(typeof message === 'string' ? message : '')
  }

  const [mutateToggle] = useMutation(TOGGLE_ZONE_SPOTLIGHT)
  const [mutateDelete] = useMutation(DELETE_ZONE_SPOTLIGHT, {
    refetchQueries: [{ query: GET_ZONE_SPOTLIGHTS }]
  })
  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    GET_ZONE_SPOTLIGHTS
  )

  const columns = [
    {
      name: t('Zone'),
      sortable: true,
      selector: 'zone.title',
      cell: row => <span>{row.zone?.title || '-'}</span>
    },
    {
      name: t('Shop type'),
      cell: row => <span>{row.shopType || t('AllShopTypes')}</span>
    },
    {
      name: t('RailTitle'),
      cell: row => <span>{row.title || '-'}</span>
    },
    {
      name: t('Stores'),
      cell: row => <span>{(row.restaurants || []).length}</span>
    },
    {
      name: t('Status'),
      cell: row => (
        <Switch
          size="small"
          checked={!!row.isActive}
          onChange={() => {
            mutateToggle({ variables: { id: row._id } })
          }}
        />
      )
    },
    {
      name: t('Action'),
      cell: row => (
        <>
          <IconButton
            aria-label="edit"
            title={t('Edit')}
            onClick={() => {
              setSavedMessage('')
              setEditTarget({
                _id: row._id,
                zone: row.zone?._id || '',
                shopType: row.shopType || ''
              })
            }}>
            <EditIcon fontSize="small" style={{ color: 'green' }} />
          </IconButton>
          <IconButton
            aria-label="delete"
            title={t('Delete')}
            onClick={() => setDeleteTarget(row)}>
            <DeleteIcon fontSize="small" style={{ color: 'red' }} />
          </IconButton>
        </>
      )
    }
  ]

  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Spotlight')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {savedMessage ? (
          <Alert
            className={globalClasses.alertSuccess}
            variant="filled"
            severity="success"
            onClose={() => setSavedMessage('')}>
            {savedMessage}
          </Alert>
        ) : null}

        {errorQuery ? (
          <span>
            `${t('Error')}! ${errorQuery.message}`
          </span>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={<TableHeader title={t('ZoneSpotlights')} />}
            columns={columns}
            data={data?.zoneSpotlights ?? []}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            customStyles={customStyles}
          />
        )}
        <Modal
          open={Boolean(editTarget)}
          onClose={() => closeEdit()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            padding: 16
          }}>
          <Box
            sx={{
              width: { xs: '100%', md: 780 },
              maxWidth: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              outline: 'none'
            }}>
            {/* Keyed by row so opening a different spotlight mounts a fresh
                form rather than reusing the previous row's list. */}
            <ZoneSpotlightComponent
              key={editTarget?._id}
              editTarget={editTarget}
              onClose={closeEdit}
            />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <ZoneSpotlightComponent
              onClose={message => {
                setAddModal(false)
                setSavedMessage(typeof message === 'string' ? message : '')
              }}
            />
          </Box>
        </Modal>
        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={`${t('Delete')}?`}
          message={t('DeleteConfirm')}
          confirmLabel={t('Delete')}
          cancelLabel={t('Cancel')}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            mutateDelete({ variables: { id: deleteTarget._id } })
            setDeleteTarget(null)
          }}
        />
      </Container>
    </>
  )
}

export default withTranslation()(ZoneSpotlights)
